if (!customElements.get('privacy-choices')) {
  customElements.define(
    'privacy-choices',
    class PrivacyChoices extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('[data-form]');
        this.status = this.querySelector('[data-status]');
        this.gpcNotice = this.querySelector('[data-gpc-notice]');
        this.inputs = Array.from(this.querySelectorAll('[data-category]'));
        this.buttons = Array.from(this.querySelectorAll('button.pc-btn'));
        this.hasGpc = navigator.globalPrivacyControl === true;
        this.onConsentSignal = this.onConsentSignal.bind(this);
      }

      connectedCallback() {
        if (this.hasGpc && this.gpcNotice) this.gpcNotice.hidden = false;

        this.inputs.forEach((input) => {
          input.addEventListener('change', () => this.updateStateLabel(input));
        });
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.save('save');
        });
        this.querySelector('[data-action="reject"]').addEventListener('click', () => this.save('reject'));
        this.querySelector('[data-action="accept"]').addEventListener('click', () => this.save('accept'));
        this.querySelector('[data-action="details"]').addEventListener('click', () => this.openDetails());

        // Keep the toggles in sync when the visitor uses the Consentmo banner.
        document.addEventListener('consentmoSignal', this.onConsentSignal);

        this.whenReady().then(() => this.render(this.currentChoices()));
      }

      disconnectedCallback() {
        document.removeEventListener('consentmoSignal', this.onConsentSignal);
      }

      onConsentSignal() {
        if (!this.saving) this.render(this.currentChoices());
      }

      // Wait (briefly) for Shopify's Customer Privacy API and Consentmo's SDK.
      async whenReady() {
        if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
          await new Promise((resolve) => {
            window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => resolve());
          });
        }

        const deadline = Date.now() + 5000;
        while (!window.Consentmo && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      currentChoices() {
        const consentmo = window.Consentmo?.currentVisitorConsent?.();
        if (consentmo) {
          return {
            analytics: consentmo.analytics,
            marketing: consentmo.marketing,
            preferences: consentmo.preferences
          };
        }

        // No decision yet: show the region default (on in opt-out regions like
        // the US, off in opt-in regions like the EU), minus anything GPC opts out.
        const privacy = window.Shopify?.customerPrivacy;
        if (privacy) {
          return {
            analytics: privacy.analyticsProcessingAllowed(),
            marketing: !this.hasGpc && privacy.marketingAllowed() && privacy.saleOfDataAllowed(),
            preferences: privacy.preferencesProcessingAllowed()
          };
        }

        return { analytics: false, marketing: false, preferences: false };
      }

      readForm() {
        return Object.fromEntries(this.inputs.map((input) => [input.dataset.category, input.checked]));
      }

      render(choices) {
        this.inputs.forEach((input) => {
          input.checked = Boolean(choices[input.dataset.category]);
          this.updateStateLabel(input);
        });
      }

      updateStateLabel(input) {
        const label = input.closest('.pc-switch')?.querySelector('[data-state-label]');
        if (label) label.textContent = input.checked ? 'On' : 'Off';
      }

      async save(mode) {
        const choices =
          mode === 'accept'
            ? { analytics: true, marketing: true, preferences: true }
            : mode === 'reject'
              ? { analytics: false, marketing: false, preferences: false }
              : this.readForm();

        this.saving = true;
        this.setBusy(true);
        this.status.textContent = 'Saving…';

        try {
          const consentmo = window.Consentmo;
          if (consentmo) {
            if (mode === 'accept') await consentmo.acceptAll();
            else if (mode === 'reject') await consentmo.declineAll();
            else await consentmo.setTrackingConsent(choices);
          }

          // Consentmo doesn't forward mixed choices or sale_of_data to Shopify,
          // so write them directly. This is what Shopify pixels and Chord read.
          await this.setShopifyConsent(choices);

          this.render(choices);
          this.status.textContent = 'Your privacy choices have been saved.';
        } catch (error) {
          console.error('Privacy choices: failed to save consent', error);
          this.status.textContent = 'Sorry, we couldn’t save your choices. Please try again.';
        } finally {
          this.saving = false;
          this.setBusy(false);
        }
      }

      setShopifyConsent(choices) {
        const privacy = window.Shopify?.customerPrivacy;
        if (typeof privacy?.setTrackingConsent !== 'function') return Promise.resolve();

        return new Promise((resolve, reject) => {
          privacy.setTrackingConsent(
            {
              analytics: choices.analytics,
              marketing: choices.marketing,
              preferences: choices.preferences,
              sale_of_data: choices.marketing
            },
            (result) => (result?.error ? reject(new Error(result.error)) : resolve())
          );
        });
      }

      openDetails() {
        if (typeof window.Consentmo?.openDialog === 'function') {
          window.Consentmo.openDialog('preferences');
        }
      }

      setBusy(isBusy) {
        this.buttons.forEach((button) => {
          button.disabled = isBusy;
        });
      }
    }
  );
}
